import { Suspense, useCallback, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { guardedLeave } from '../lib/unsavedGuard';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { qk, type Property } from '@immoaktas/api-client';
import { enumLabel } from '@immoaktas/i18n';
import { api, endSession, setAccount, useAuth, role } from '../lib/api';
import { can } from '../lib/hooks';
import { webEnum } from '../i18n/labels';
import { AccountMenu, AppShell, Icon, PageTrailProvider, Skeleton, type NavItem } from '../ui';
import { Paywall } from '../routes/landlord/parts/Paywall';
import { UploadSheetHost } from '../routes/landlord/parts/UploadSheet';
import { LegalFooter, LegalMenuLinks } from './LegalLinks';
import { AccountNotices } from './LegalConsent';
import { navLevel, propertyTrail, sectionPath, switchTarget, tenancyTabBarOf, unitPath, unitTabBarOf, unitTrail, type PropertySection, type Trail } from './scope';
import { forgetPropertyScope, useLastUnit, usePropertyScope } from './useScope';
import { FileDropLayer, QuickUploadButton } from './QuickUpload';
import { UnitSwitch } from './UnitSwitch';
import { tenancyTabBarLabel, unitTabBarLabel } from './levelLabels';
import { tenancyTitle } from '../routes/landlord/parts/unit/context';
import { byFloor } from '../routes/landlord/parts/objects';
import { useNavCounts, usePropertyFacts } from './navData';
import { PortfolioNav, PropertyNav } from './LevelNav';
import { LevelTopBar } from './LevelTopBar';
import { AgentHost, useAgentColumn } from '../agent/AgentHost';

const SWITCH_ID = 'objekt-switch';

/** "Objekt wechseln" in the property card: another property (same section) or back to "Alle Objekte". */
function PropertySwitch({ properties, value, onChange }: { properties: Property[]; value: string; onChange: (id: string | null) => void }) {
  const { t } = useTranslation();
  const name = properties.find((p) => p.id === value)?.name;
  // the property's name is the switcher: the full name stays readable, the native select lies invisibly on top of it
  return (
    <label className="here-switch">
      <span className="here-name">{name}</span>
      <Icon name="chevronDown" size={16} strokeWidth={2} aria-hidden />
      <select id={SWITCH_ID} value={value} aria-label={t('shell.level.switch')} title={t('shell.level.switch')} onChange={(e) => onChange(e.target.value || null)}>
        {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        <option value="">{t('shell.level.all')}</option>
      </select>
    </label>
  );
}

/**
 * Landlord app frame with three navigation levels (design/NavKonzept, design/Sidebar, docs/09-screens.md):
 * level 1 "Alle Objekte" (dark sidebar: Übersicht · Objekte · Belege · Abrechnungen · Zahlungen · Mehr, the
 * properties as cards), level 2 inside one property (light sidebar, way back, property card with switcher:
 * Übersicht · Einheiten · Belege · Abrechnung · Zahlungen · Zähler · Mehr) and level 3 inside one unit (same light
 * sidebar, "Einheiten" highlighted; unit switcher and the unit's tabs are in the page header). Every page header inside
 * a property starts with the trail "Alle Objekte › <Objekt> › …" and a "← Zurück zu <parent>" button. "+ Beleg" is on
 * every page (top bar), and files dropped anywhere open the upload sheet. On small screens the sidebar is a drawer and
 * the top bar names the level.
 */
export default function LandlordShell() {
  const { t } = useTranslation();
  const me = useAuth((s) => s.me);
  const accountId = useAuth((s) => s.accountId);
  const nav = useNavigate();
  const { pathname } = useLocation();
  const scope = usePropertyScope();
  const props = useQuery({ queryKey: qk.properties, queryFn: () => api.properties.list() });
  const properties = props.data ?? [];
  // a remembered or typed id that is not a property of this account falls back to "Alle Objekte"
  const property = scope.propertyId ? properties.find((p) => p.id === scope.propertyId) : undefined;
  const pid = property?.id ?? (props.isPending ? scope.propertyId : null);
  const level = navLevel(scope.path, pid);
  const inProperty = level !== 'portfolio';
  const shellLevel = inProperty ? 'objekt' : 'portfolio';
  const lastUnit = useLastUnit((s) => (pid ? s.byProperty[pid] : undefined));
  const c = useNavCounts(inProperty ? pid : null);
  const units = useQuery({
    queryKey: qk.units(pid ?? ''),
    queryFn: () => api.properties.units(pid!),
    enabled: inProperty && !!pid,
    staleTime: 60_000,
  });
  const facts = usePropertyFacts(t, properties);
  const billing = useQuery({ queryKey: qk.billing, queryFn: () => api.subscription.summary(), staleTime: 300_000 });
  const membership = me?.memberships.find((m) => m.accountId === accountId);
  const r = role();
  const count = (n: number, key: string): Pick<NavItem, 'badge' | 'badgeLabel'> => (n > 0 ? { badge: n, badgeLabel: t(key, { count: n }) } : {});

  const agentColumn = useAgentColumn();
  const [drawer, setDrawer] = useState(false);
  const [focusSwitch, setFocusSwitch] = useState(false);
  const closeDrawer = useCallback(() => setDrawer(false), []);
  // every navigation closes the drawer (the new page is what the landlord asked for)
  useEffect(() => setDrawer(false), [pathname]);
  useEffect(() => {
    if (drawer && focusSwitch) document.getElementById(SWITCH_ID)?.focus();
    if (!drawer) setFocusSwitch(false);
  }, [drawer, focusSwitch]);

  const sectionLabel: Record<PropertySection, string> = {
    uebersicht: t('shell.nav.overview'),
    einheiten: t('shell.nav.units'),
    vertraege: t('shell.nav.tenants'),
    belege: t('shell.nav.documents'),
    zaehler: t('shell.nav.meters'),
    zahlungen: t('shell.nav.payments'),
    abrechnungen: t('shell.nav.statements'),
    umlage: t('shell.nav.umlage'),
    chronik: t('shell.nav.history'),
    hausgeld: t('shell.nav.hoa'),
    einstellungen: t('shell.nav.propertySettings'),
  };
  const general: NavItem[] = [
    { to: '/mitteilungen', label: t('shell.nav.notifications'), icon: 'notifications', ...count(c.unread, 'shell.badge.unread') },
    { to: '/fristen', label: t('shell.nav.deadlines'), icon: 'deadlines', ...count(c.overdue, 'shell.badge.overdue') },
    { to: '/einstellungen', label: t('shell.nav.settings'), icon: 'settings', also: ['/export'] },
  ];
  // house order like everywhere else: server order (compareHouseOrder), grouped by floor
  // switching the unit keeps the open tab (Zähler stays Zähler); from a tenancy it opens the other unit's tenancies
  const openTab = scope.path.kind === 'unit' ? (scope.path.tenancyId ? 'mietverhaeltnisse' : scope.path.tab) : undefined;
  const unitChoices = property ? byFloor(property, units.data ?? []).map((u) => ({ id: u.id, to: unitPath(u.id, openTab), label: u.label })) : [];
  const upload = can(r, 'upload');
  // switching the property leaves the page: a page with unsaved edits asks first
  const choose = (id: string | null) => guardedLeave(() => {
    if (!id) forgetPropertyScope();
    nav(switchTarget(pathname, id, properties.find((p) => p.id === id)?.type === 'weg_unit'));
  });
  const plan = billing.data ? webEnum(t, 'plan', billing.data.plan) : null;
  const sub = [membership ? enumLabel(t, 'role', membership.role) : null, plan].filter(Boolean).join(' · ');
  const footer = me && (
    <AccountMenu name={membership?.accountName ?? me.user.name} avatarName={me.user.name} sub={sub} email={me.user.email} label={t('shell.account.menu')}>
      <NavLink to="/einstellungen"><Icon name="settings" />{t('shell.nav.settings')}</NavLink>
      {me.tenant && <NavLink to="/mieter"><Icon name="user" />{t('app.nav.viewAsTenant')}</NavLink>}
      <LegalMenuLinks />
      <button type="button" onClick={() => void endSession().then(() => nav('/login'))}><Icon name="logout" />{t('nav.logout')}</button>
    </AccountMenu>
  );

  let sidebar;
  if (inProperty && pid) {
    const at = (s: PropertySection) => sectionPath(pid, s);
    // a property with one unit (ETW, house): "Einheiten" opens that unit right away
    const single = units.data?.length === 1 ? units.data[0]! : null;
    const items: NavItem[] = [
      { to: at('uebersicht'), label: sectionLabel.uebersicht, icon: 'dashboard', end: true, also: [`${at('uebersicht')}/akte`] },
      { to: single ? unitPath(single.id) : at('einheiten'), label: sectionLabel.einheiten, icon: 'grid', also: [at('einheiten'), '/einheiten', `${at('uebersicht')}/import`] },
      { to: at('belege'), label: sectionLabel.belege, icon: 'receipts', also: [`${at('uebersicht')}/kosten`], ...count(c.documents, 'shell.badge.documents') },
      { to: at('abrechnungen'), label: sectionLabel.abrechnungen, icon: 'statements', also: ['/abrechnungen', `${at('uebersicht')}/zeitraeume`] },
      { to: at('zahlungen'), label: sectionLabel.zahlungen, icon: 'payments', ...count(c.arrears, 'shell.badge.arrears') },
      { to: at('zaehler'), label: sectionLabel.zaehler, icon: 'meters', ...count(c.readings, 'shell.badge.readings') },
    ];
    const more: NavItem[] = [
      { to: at('vertraege'), label: sectionLabel.vertraege, icon: 'tenants', also: ['/mietverhaeltnisse'] },
      { to: at('umlage'), label: sectionLabel.umlage, icon: 'allocate' },
      ...(property?.type === 'weg_unit' ? [{ to: at('hausgeld'), label: sectionLabel.hausgeld, icon: 'building' as const }] : []),
      { to: at('chronik'), label: sectionLabel.chronik, icon: 'history' },
      { to: at('einstellungen'), label: sectionLabel.einstellungen, icon: 'settings' },
    ];
    sidebar = property ? (
      <PropertyNav
        property={property}
        facts={facts.get(property.id)}
        items={items}
        more={more}
        general={general}
        switcher={<PropertySwitch properties={properties} value={property.id} onChange={choose} />}
        footer={footer}
      />
    ) : (
      <nav className="sidebar level-nav objekt" aria-label={t('shell.level.inProperty')} aria-busy="true">
        <Skeleton h={48} />
        <Skeleton h={180} />
        <Skeleton count={8} h={22} />
      </nav>
    );
  } else {
    const main: NavItem[] = [
      { to: '/', label: t('shell.nav.dashboard'), icon: 'dashboard', end: true },
      { to: '/objekte', label: t('shell.nav.properties'), icon: 'properties', end: true },
      { to: '/belege', label: t('shell.nav.documents'), icon: 'receipts', ...count(c.documents, 'shell.badge.documents') },
      { to: '/abrechnungen', label: t('shell.nav.statements'), icon: 'statements', also: ['/export/steuer'] },
      { to: '/zahlungen', label: t('shell.nav.payments'), icon: 'payments', ...count(c.arrears, 'shell.badge.arrears') },
    ];
    const more: NavItem[] = [
      { to: '/personen', label: t('shell.nav.tenants'), icon: 'tenants', also: ['/mietverhaeltnisse'] },
      { to: '/zaehler', label: t('shell.nav.meters'), icon: 'meters', ...count(c.readings, 'shell.badge.readings') },
      { to: '/fristen', label: t('shell.nav.deadlines'), icon: 'deadlines', ...count(c.overdue, 'shell.badge.overdue') },
      { to: '/mitteilungen', label: t('shell.nav.notifications'), icon: 'notifications', ...count(c.unread, 'shell.badge.unread') },
      { to: '/export/steuer', label: t('workspace.nav.export'), icon: 'download', end: true },
      { to: '/einstellungen', label: t('shell.nav.settings'), icon: 'settings' },
    ];
    sidebar = (
      <PortfolioNav
        main={main}
        more={more}
        properties={properties}
        facts={facts}
        canCreate={can(r, 'write')}
        top={me && me.memberships.length > 1 ? (
          <select className="account-switch" aria-label={t('app.nav.chooseAccount')} value={accountId ?? ''} onChange={(e) => { const next = e.target.value; guardedLeave(() => { setAccount(next); forgetPropertyScope(); nav('/'); }); }}>
            {me.memberships.map((m) => <option key={m.accountId} value={m.accountId}>{m.accountName}</option>)}
          </select>
        ) : null}
        footer={footer}
      />
    );
  }

  let trail: (Trail & { tools?: ReactNode }) | null = null;
  if (inProperty && pid && property && scope.path.kind === 'unit') {
    const p = scope.path;
    trail = unitTrail(p, pid, {
      all: t('shell.level.all'),
      property: property.name,
      units: sectionLabel.einheiten,
      unit: scope.unit?.label ?? t('fields.unit'),
      // the trail uses the words of the tab bar ("Mietverhältnis", "Konto & Mahnung")
      tab: unitTabBarLabel(t, unitTabBarOf(p.tab)),
      tenancy: scope.tenancy ? t('shell.trail.tenancy', { name: tenancyTitle(t, scope.tenancy) }) : t('fields.tenancy'),
      tenancyTab: p.tenancyTab ? tenancyTabBarLabel(t, tenancyTabBarOf(p.tenancyTab)) : undefined,
    });
    trail = { ...trail, tools: <UnitSwitch units={unitChoices} activeId={p.unitId} here /> };
  } else if (inProperty && pid && property && scope.section)
    trail = propertyTrail(pathname, pid, scope.section, { all: t('shell.level.all'), property: property.name, section: sectionLabel[scope.section] });
  // the units of the house: switcher in the header of "Einheiten" (it replaced the unit list in the sidebar)
  if (trail && scope.path.kind === 'property' && scope.section === 'einheiten' && unitChoices.length > 1)
    trail = { ...trail, tools: <UnitSwitch units={unitChoices} activeId={lastUnit?.id} /> };

  return (
    <AppShell
      level={shellLevel}
      drawer={{ open: drawer, onClose: closeDrawer, label: inProperty && property ? t('shell.level.navProperty', { name: property.name }) : t('shell.level.navAll') }}
      topBar={
        <LevelTopBar
          property={inProperty ? (property ?? { name: '' }) : null}
          back={level === 'einheit' ? trail?.back : null}
          open={drawer}
          onMenu={() => setDrawer(true)}
          onSwitch={() => { setFocusSwitch(true); setDrawer(true); }}
          upload={upload ? <QuickUploadButton propertyId={inProperty ? pid : null} compact /> : null}
        />
      }
      bar={upload ? <QuickUploadButton propertyId={inProperty ? pid : null} /> : undefined}
      sidebar={<div className="level-swap" key={shellLevel}>{sidebar}</div>}
      aside={<AgentHost />}
      asideOpen={agentColumn}
      after={<><Paywall /><UploadSheetHost /><FileDropLayer propertyId={inProperty ? pid : null} enabled={upload} /></>}
    >
      <PageTrailProvider value={trail}>
        <AccountNotices />
        <Suspense fallback={<Skeleton count={6} h={24} />}>
          <Outlet />
        </Suspense>
        {/* § 5 DDG: Impressum one click away on every page behind the login, not only in the account menu */}
        <LegalFooter />
      </PageTrailProvider>
    </AppShell>
  );
}
